/**
 * *******************************************************************************************************
 *
 * V1.2
 *
 * User: tomers
 * Date: 4/22/13
 * Visual PDF: https://drive.google.com/a/liveperson.com/?tab=mo#folders/0BzCszeQVU73ycVZXS0RYQkwxd28
 *
 * Changes:
 *
 * Change inputSize attribute to 'permutation' attribute.
 * As for now we support 3 permutations (A, B and D).
 * Options:
 * permutation - optional. One of the options in the static InlineInput.PERMUTATION enum. PERMUTATION.B is the default
 *
 *
 * ********************************************************************************************************
 *
 * V1.1
 *
 * User: tomers
 * Date: 3/21/13
 * Visual PDF: https://docs.google.com/a/liveperson.com/file/d/0B7O_LAPhjkbkLUZpZnRnUU9hWms/edit
 *
 * This widget is used to implement an inline textarea component on the page.
 * Currently we support 2 sizes (medium and xxlarge).
 * Options:
 * el - mandatory. jQuery element. The element on the page where the component will be created.
 * label - mandatory. String. The label appears when there is no value on non-editable mode. Should be the 'data-label' attribute on the 'el' HTML element (also, can be sent as part of the options)
 * model - mandatory. Marionette View. The model of the view that using this component.
 * bindMap - mandatory. Object. Mapping between the component model attributes and the external view model attribute. For example: {'value':'description'}
 * placeholder - optional. String. The placeholder appears when there is no value on editable mode. Should be the 'data-placeholder' attribute on the 'el' HTML element (also, can be sent as part of the options)
 * inputSize - optional. One of the options in the static InlineTextarea.SIZE enum (MEDIUM / XXLARGE). MEDIUM is the default
 *
 * Using Example:
 *
 * template:
 *  <div class="campaign-description" data-placeholder="{{#_i18n}}LECampaigns.campaignDetails.addDescription.placeholder{{/_i18n}}" data-label="{{#_i18n}}LECampaigns.campaignDetails.addDescription.label{{/_i18n}}"></div>
 *
 *  js:
 *  var campaignDescription = new InlineTextarea({
 *      model:this.model,
 *      bindMap : {
 *          'value' : 'description'
 *      },
 *      el:this.ui.campaignDescription
 *  });
 *  campaignDescription.render();
 *
 *
 * *******************************************************************************************************
 *
 */
define(function (require) {
    "use strict";

    var Marionette = require('marionette');
    var template = require('ui/inline-textarea/templates/inlineTextarea.tmpl');
    var InlineTextareaModel = require('ui/inline-textarea/js/models/InlineTextareaModel');
    var Bubble = require("ui/bubble/Bubble");
    var _ = require("underscore");
    var $ = require("jquery");
    require('ui.plugins/jquery.inputCursor');

    var EMPTY_VALUE = "";

    var InlineTextareaView = Marionette.ItemView.extend({
        template:template,
        withUIComponents: true,
        errorMsg:null,
        // event handlers
        events:{
            "click div.add-description span":"onLabelClick", // triggered when user clicks on the label of '+add description'
            "click div.description":"onTextClick", // triggered when user clicks on existing description (in non-editable mode)
            "blur textarea.description":"onTextareaBlur", // triggered when user is blurred the description textarea (in editable mode)
            "focus textarea.description":"onTextareaFocus", // triggered when user is focus on the description textarea (in editable mode)
            "input textarea.description":"onTextareaChange", // triggered when there is a change (any change) in the description value (in editable mode)
            "cut textarea.description":"onTextareaChange", // triggered when there is a change (any change) in the description value (in editable mode)
            "paste textarea.description":"onTextareaChange", // triggered when there is a change (any change) in the description value (in editable mode)
            "keyup textarea.description":"onTextareaChange" // triggered when there is a change (any change) in the description value (in editable mode)
        },

        ui:{
            inlineTextAreaComponentWrapper: ".inline-textarea",
            textarea:"textarea.description",
            text:"div.description",
            label:"div.add-description"
        },

        inputSize:null,

        initialize:function (options) {
            this.eventsNamespace = 'inlineTextArea' + Math.random();

            this.container = options.container;

            var inlineTextareaModel = new InlineTextareaModel();

            var placeholder = $(options.container).attr("data-placeholder") || options.placeholder;
            inlineTextareaModel.set("placeholder", placeholder);

            var label = $(options.container).attr("data-label") || options.label;
            inlineTextareaModel.set("label", label);

            var initValue = options.value || EMPTY_VALUE;
            inlineTextareaModel.set(InlineTextareaView.VALUE, initValue);
            inlineTextareaModel.set("lastKnownGoodValue", initValue);

            var enabled = _.has(options, "enabled") ? options.enabled : inlineTextareaModel.get("enabled");
            inlineTextareaModel.set("enabled", enabled);

            if(_.has(options, "maxChars")){
                inlineTextareaModel.set("maxChars", options.maxChars);
            }

            this.model = inlineTextareaModel;
            this.permutation = options.permutation || InlineTextareaView.PERMUTATION.B;
            this.theme = options.theme || InlineTextareaView.THEME.DEFAULT;
            this.errorPosition = options.errorPosition || InlineTextareaView.ERROR_POSITION.BOTTOM;
        },

        /**
         * Attaches the size class to the elements on the DOM (by the options inputSize attribute)
         * Also, decide what to show, the label (if there is no description) or the description text (if there is one)
         */
        onRender:function () {
            this.container.append(this.$el);

            var classByPermutation = this._getClassByPermutation();
            this.ui.textarea.addClass(classByPermutation);
            this.ui.text.addClass(classByPermutation);
            this.ui.inlineTextAreaComponentWrapper.addClass(this.theme);
            this.heightTxtAreaCalc = this._calculateOneLineInTxtArea(); // this calculate is done in order to enlarge the textarea while typing on demand when exceeding the maximum text in the current size

            if (this.model.get(InlineTextareaView.VALUE) && this.model.get(InlineTextareaView.VALUE).length > 0) {
                this.showText();
            }
            else {
                this.showLabel();
            }

            this._bindDocumentEvents();
        },

        _bindDocumentEvents: function() {
            var that = this;

            $(document.body).on("keydown." + this.eventsNamespace, ".le", function (event) {
                if (event.keyCode === 27) { //ESC
                    that.onTextareaBlur();
                }
            });
        },

        _unbindDocumentEvents: function() {
            $(document.body).off("." + this.eventsNamespace);
        },

        /**
         * Set the description content in the HTML element to show it in non-editable mode.
         * The converted text is shown with css property { white-space: pre-wrap }
         * @private
         */
        _setHTMLTextByTextArea:function (val) {
            val = val || this.model.get(InlineTextareaView.VALUE);
            this.ui.text.find("div").text(val);
        },

        /**
         * Returns the correct widget class by its permutation
         * @return {String} - the relevant class to attach to the HTML elements on the page
         * @private
         */
        _getClassByPermutation:function () {
            var retClass = "inline-textarea-perm-" + this.permutation;

            return retClass;
        },

        /**
         * Shows the description value in non-editable mode
         */
        showText:function (val) {
            this.ui.text.show();
            this._setHTMLTextByTextArea(val);
            this.ui.textarea.hide();
            this.ui.label.hide();
        },

        /**
         * Show the description label (when there is no description)
         */
        showLabel:function () {
            this.ui.text.hide();
            this.ui.textarea.hide();
            this.ui.label.show();
        },

        /**
         * Show the description value in editable mode textarea
         *
         * NOTE: We are not handling the placeholder in NON HTML5 browsers.
         * This is because we don't have a reason to put a placeholder and just remove it when focus the textarea.
         */
        showTextArea:function () {
            this.ui.textarea.val(this.model.get(InlineTextareaView.VALUE));
            this.ui.text.hide();
            this.ui.textarea.show();
            this.ui.label.hide();
            this.textAreaResize();
        },

        /**
         * Triggered when the user clicks on the container of the textarea
         * NOTE: This public method (that being used only in SurveyCanvasView) doesn't support the enabled/disabled feature
         */
        onContainerClick: function(){
            this.ui.text.hide();
            this.ui.textarea.show();
            this.ui.label.hide();
            this.setFocus();
            this.textAreaResize();
        },

        onTextareaFocus:function () {
            if (this.ui.textarea.hasClass("error") && this.errorMsg) {
                this.uiComponents.host$.createAndRender("InlineTextareaError", {
                    type:Bubble,
                    options:{
                        targetControl:this.ui.textarea,
                        text:this.errorMsg,
                        position:this.errorPosition,
                        icon:Bubble.ICON.ERROR,
                        showCloseButton:false
                    }
                });
            }
        },

        /**
         * Triggered when the user clicks out of the textarea (editable mode)
         * In this case we save the value to the model and shows the label or the value itself (if exists)
         */
        onTextareaBlur:function () {
            this.setError(false);

            this.textareaIsChanged();

            var newVal = this.model.get('lastKnownGoodValue').trim();
            if (EMPTY_VALUE === newVal) {
                this.showLabel();
            }
            else {
                this.showText(newVal);
            }

            this._setValue(newVal);
        },

        /**
         * Triggered when user clicks on existing description text (in non-editable mode) and moves to editable mode
         */
        onTextClick:function () {
            if (this.model.get("enabled")) {
                this.showTextArea();
                this.setFocus();
            }
        },

        /**
         * Triggered when user clicks on 'add description label' and moves to editable mode
         */
        onLabelClick:function () {
            if (this.model.get("enabled")) {
                this.showTextArea();
                this.setFocus();
            }
        },

        /**
         * Triggered on any change in the input value.
         * If the new value is valid, it's saved to the 'lastKnownGoodValue' attribute on the model.
         * If the new value is not valid, we set the textarea value to be the 'lastKnownGoodValue' from the model
         *
         * NOTE: setTimeout is used to solve an issue with Firefox.
         * Firefox doesn't catch an input/change event when user uses right click and paste/cancel (when not using setTimeout 0)
         */
        onTextareaChange:function () {
            var that = this;
            setTimeout(function () {
                that.textareaIsChanged();
            }, 0);
        },

        textareaIsChanged:function () {
            var newVal = this.ui.textarea.val();
            if (this.model.validate(newVal.trim())) {
                this.model.set({'lastKnownGoodValue':newVal});
            }
            else {
                this.ui.textarea.val(this.model.get('lastKnownGoodValue'));
            }
            this.textAreaResize();
        },

        /**
         * Handles the textarea resizing dynamically by the amount of text
         */
        textAreaResize:function () {
            this.ui.textarea.css("height", "auto");
            this.ui.textarea.height(this.ui.textarea[0].scrollHeight - this.heightTxtAreaCalc);
        },

        /**
         * Calculation that used by textAreaResize method to resize the textarea dynamically
         * @return {Number} - The differentiation between the height and the scrollHeight of the textarea (when there is no value in it).
         * @private
         */
        _calculateOneLineInTxtArea:function () {
            var oldVal = this.ui.textarea.val();
            this.ui.textarea.val(EMPTY_VALUE);
            var h = this.ui.textarea.height();
            var s = this.ui.textarea[0].scrollHeight;
            this.ui.textarea.val(oldVal);
            return (s - h);
        },

        setFocus:function () {
            this.ui.textarea.focus();
            this.ui.textarea.inputcursor("focusEnd");
        },

        setEnabled: function(mode) {
            this.model.set({"enabled": mode});
        },

        /**
         * Set the value in the internal model and triggers an event to whoever is listening to change on this view-component
         * @param value. String. The value to update the model with.
         * @param options Object. silent:true will not trigger the change event
         * @private
         */
        _setValue:function (value, options) {
            options = options || {};
            if (value !== this.model.get(InlineTextareaView.VALUE)) {
                this.model.set(InlineTextareaView.VALUE, value);
                this.model.set('lastKnownGoodValue', value);

                if (!options.silent) {
                    this.trigger("change", value);
                }
            }
        },

        /**
         * Public method to change the value in the component.
         * As default, if the value is set from other view and not through the DOM, we are not triggers the event because we assume that whoever call this method already knows that the value has been changed.
         * @param value. String. The value to update the model with.
         */
        setValue:function (value, options) {
            options = options || {};
            options.silent = _.has(options, "silent") ? options.silent : true;

            this._setValue(value, options);
        },

        getValue:function () {
            return this.model.get(InlineTextareaView.VALUE);
        },

        setError:function (isError, msg) {
            if (isError === true) {
                this.showTextArea();
                this.ui.textarea.addClass("error");
                this.errorMsg = msg;
            }
            else {
                this.ui.textarea.removeClass("error");
                this.errorMsg = null;
                if (this.uiComponents.InlineTextareaError) {
                    this.uiComponents.InlineTextareaError.close();
                }
            }
        },

        onClose:function (){
            this._unbindDocumentEvents();
        }
    });

    /**
     * Public static eNum of the optional permutation
     */
    InlineTextareaView.PERMUTATION = {
        A:1,
        B:2,
        C:3,
        D:4,
        E:5,
        F:6
    };
    InlineTextareaView.THEME = {
        DEFAULT: "default-theme",
        NIGHT: "night-theme"
    };
    InlineTextareaView.ERROR_POSITION = Bubble.POSITION;
    // Public static variable that being used by the widget and this view to get and set the value in the relevant model attribute
    InlineTextareaView.VALUE = "value";

    return InlineTextareaView;
});

